import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Search, Download } from 'lucide-react';
import { fetchRetiredEmployeesReport } from '../../services/reportsApi';
import * as XLSX from 'xlsx';
import { formatDate } from '../../utils/dateUtils';

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const RetiredEmployeesReportView = () => {
  const { hasPermission } = useAuth();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const YEARS = Array.from({length: 10}, (_, i) => String(currentYear + i));

  const [fromYear, setFromYear] = useState(String(currentYear));
  const [fromMonth, setFromMonth] = useState(String(currentMonth).padStart(2, '0'));
  
  const [toYear, setToYear] = useState(String(currentYear));
  const [toMonth, setToMonth] = useState('12');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (fromYear === String(currentYear) && parseInt(fromMonth, 10) < currentMonth) {
      setFromMonth(String(currentMonth).padStart(2, '0'));
    }
    if (parseInt(toYear, 10) < parseInt(fromYear, 10)) {
      setToYear(fromYear);
    }
  }, [fromYear]);

  useEffect(() => {
    if (toYear === fromYear && parseInt(toMonth, 10) < parseInt(fromMonth, 10)) {
      setToMonth(fromMonth);
    }
  }, [fromYear, fromMonth, toYear]);

  const loadData = async () => {
    if (!fromYear || !fromMonth || !toYear || !toMonth) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError('');
    
    try {
      const fromStr = `${fromYear}-${fromMonth}`;
      const toStr = `${toYear}-${toMonth}`;
      const response = await fetchRetiredEmployeesReport(fromStr, toStr, abortControllerRef.current.signal);
      setData(response.items || []);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to fetch retired employees report');
      }
    } finally {
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (fromYear && fromMonth && toYear && toMonth) {
      loadData();
    }
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fromYear, fromMonth, toYear, toMonth]);

  const filteredData = data.filter(emp => {
    if (!searchQuery) return true;
    const lowerQuery = searchQuery.toLowerCase();
    return (
      (emp.emp_code && emp.emp_code.toLowerCase().includes(lowerQuery)) ||
      (emp.full_name && emp.full_name.toLowerCase().includes(lowerQuery)) ||
      (emp.department && emp.department.toLowerCase().includes(lowerQuery)) ||
      (emp.designation && emp.designation.toLowerCase().includes(lowerQuery))
    );
  });

  const downloadExcel = () => {
    if (filteredData.length === 0) return;

    const exportData = filteredData.map(item => ({
      'Employee Code': item.emp_code,
      'Employee Name': item.full_name,
      'Department': item.department || '-',
      'Designation': item.designation || '-',
      'Retirement Date': formatDate(item.retirement_date, '-'),
      'Retirement Status': item.retirement_status,
      'Retirement Category': item.retirement_type || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Upcoming Retirements');
    XLSX.writeFile(workbook, `Upcoming_Retirements_${fromYear}_${fromMonth}_to_${toYear}_${toMonth}.xlsx`);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filters Area */}
      <div className="bg-white p-4 border-b border-gray-200">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center space-x-6">
            <div className="flex space-x-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                  From Month
                </label>
                <select
                  value={fromMonth}
                  onChange={(e) => setFromMonth(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[120px]"
                >
                  {MONTHS.filter(m => fromYear !== String(currentYear) || parseInt(m.value, 10) >= currentMonth).map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                  From Year
                </label>
                <select
                  value={fromYear}
                  onChange={(e) => setFromYear(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                >
                  {YEARS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                  To Month
                </label>
                <select
                  value={toMonth}
                  onChange={(e) => setToMonth(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[120px]"
                >
                  {MONTHS.filter(m => toYear !== fromYear || parseInt(m.value, 10) >= parseInt(fromMonth, 10)).map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                  To Year
                </label>
                <select
                  value={toYear}
                  onChange={(e) => setToYear(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                >
                  {YEARS.filter(y => parseInt(y, 10) >= parseInt(fromYear, 10)).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm w-48 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            {hasPermission('report:export') && (
<button
              onClick={downloadExcel}
              disabled={filteredData.length === 0}
              className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
)}
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-auto bg-gray-50 p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retirement Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retirement Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retirement Category</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">Loading data...</p>
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((emp, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{emp.emp_code}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">{emp.full_name}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{emp.department || '-'}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{emp.designation || '-'}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(emp.retirement_date, '-')}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          emp.retirement_status === 'Retired'
                            ? 'bg-gray-100 text-gray-800'
                            : emp.retirement_status === 'Retiring today'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {emp.retirement_status}
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{emp.retirement_type || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No employees found retiring in this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetiredEmployeesReportView;
